import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ShareDialog } from "../../components/ShareDialog";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";
import { MoreHorizontal, Plus, Lock, FileDown, Trash2, Search, Share2, ShieldCheck, Ban, ThumbsUp, ThumbsDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DevisListPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [shareItem, setShareItem] = useState(null);
  const [mentionsOpen, setMentionsOpen] = useState(false);
  const [mentionsText, setMentionsText] = useState("");
  const [mentionsSaving, setMentionsSaving] = useState(false);

  const openMentions = async () => {
    try {
      const { data } = await api.get("/devis/settings/mentions-par-defaut");
      setMentionsText(data.mentions_legales || "");
      setMentionsOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveMentions = async () => {
    setMentionsSaving(true);
    try {
      await api.put("/devis/settings/mentions-par-defaut", { mentions_legales: mentionsText });
      toast.success("Mentions légales par défaut enregistrées (nouveaux devis uniquement)");
      setMentionsOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setMentionsSaving(false);
    }
  };

  const load = () => api.get("/devis").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (it.numero || "").toLowerCase().includes(q) || (it.client_nom || "").toLowerCase().includes(q) || (it.vendeur_nom || "").toLowerCase().includes(q);
  });

  const remove = async (id) => {
    try {
      await api.delete(`/devis/${id}`);
      toast.success("Devis supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="Devis">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="relative w-full sm:max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="devis-search-input" placeholder="Rechercher par n°, client, vendeur..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex items-center gap-2">
          {hasPerm(user, "devis", "create") && (
            <Link to="/devis/new" className="flex-1 sm:flex-none">
              <Button data-testid="devis-add-button" className="gap-2 shrink-0 w-full sm:w-auto"><Plus size={16} /> Nouveau devis</Button>
            </Link>
          )}
          {hasPerm(user, "devis", "edit") && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" data-testid="devis-menu-button" title="Options des devis"><MoreHorizontal size={18} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="devis-menu-mentions" onClick={openMentions}>Mentions légales par défaut</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="devis-list">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-secondary text-left">
            <tr><th className="p-3">N°</th><th className="p-3">Date</th><th className="p-3">Client</th><th className="p-3">Statut</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`devis-row-${it.id}`}>
                <td className="p-3">
                  {it.numero}
                  {it.is_shared_to_me && (
                    <span className="block text-xs text-muted-foreground" data-testid={`devis-shared-badge-${it.id}`}>
                      Partagé par {it.shared_by_label} ({it.share_mode === "write" ? "écriture" : "lecture"})
                    </span>
                  )}
                </td>
                <td className="p-3">{it.date}</td>
                <td className="p-3">{it.client_nom}</td>
                <td className="p-3">
                  <div className="flex flex-col gap-1 items-start">
                    {it.annule ? (
                      <span className="inline-flex items-center gap-1 text-destructive" data-testid={`devis-status-annule-${it.id}`}><Ban size={13} />Annulé</span>
                    ) : it.status === "cloture" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600" data-testid={`devis-status-cloture-${it.id}`}><ShieldCheck size={13} />Clôturé</span>
                    ) : (
                      <span>{it.status}</span>
                    )}
                    {!it.annule && it.decision === "accepte" && (
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs" data-testid={`devis-decision-accepte-${it.id}`}><ThumbsUp size={12} />Accepté</span>
                    )}
                    {!it.annule && it.decision === "refuse" && (
                      <span className="inline-flex items-center gap-1 text-destructive text-xs" data-testid={`devis-decision-refuse-${it.id}`}><ThumbsDown size={12} />Refusé</span>
                    )}
                    {!it.annule && it.expire && (
                      <span className="inline-flex items-center gap-1 text-amber-600 text-xs" data-testid={`devis-expire-badge-${it.id}`}><AlertTriangle size={12} />Expiré</span>
                    )}
                    {it.transforme_facture_numero && (
                      <span className="text-xs text-muted-foreground" data-testid={`devis-transforme-badge-${it.id}`}>
                        → Facture {it.transforme_facture_numero}
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3 text-right">
                  {it.can_open ? (
                    <div className="flex justify-end items-center gap-3">
                      <Link to={`/devis/${it.id}`} data-testid={`devis-open-${it.id}`} className="text-primary text-sm">
                        {hasPerm(user, "devis", "edit") ? "Modifier" : "Voir"}
                      </Link>
                      <a href={`${api.defaults.baseURL}/devis/${it.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`devis-pdf-${it.id}`} title="PDF"><FileDown size={15} /></a>
                      {hasPerm(user, "partage_document") && !it.is_shared_to_me && (
                        <button data-testid={`devis-share-${it.id}`} onClick={() => setShareItem(it.id)} title="Partager"><Share2 size={15} /></button>
                      )}
                      {hasPerm(user, "devis", "delete") && !it.is_shared_to_me && it.status !== "cloture" && (
                        <button data-testid={`devis-delete-${it.id}`} onClick={() => remove(it.id)} title="Supprimer" className="text-destructive"><Trash2 size={15} /></button>
                      )}
                    </div>
                  ) : (
                    <Lock size={14} className="text-muted-foreground inline" data-testid={`devis-locked-${it.id}`} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ShareDialog open={!!shareItem} onOpenChange={(v) => !v && setShareItem(null)} module="devis" itemId={shareItem} onShared={load} />
          <Dialog open={mentionsOpen} onOpenChange={setMentionsOpen}>
        <DialogContent className="sm:max-w-3xl" data-testid="devis-mentions-dialog">
          <DialogHeader><DialogTitle>Mentions légales par défaut</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Ce texte sera pré-rempli sur chaque nouveau devis et imprimé en annexe du PDF. Les devis déjà créés ne sont pas modifiés.
          </p>
          <Textarea
            data-testid="devis-mentions-default-textarea"
            className="h-80"
            value={mentionsText}
            onChange={(e) => setMentionsText(e.target.value)}
          />
          <DialogFooter>
            <Button onClick={saveMentions} disabled={mentionsSaving} data-testid="devis-mentions-default-save">
              {mentionsSaving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
</Layout>
  );
}
